import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Lock, ShieldCheck, AlertTriangle, RefreshCcw, Copy } from 'lucide-react';

function errMsg(e, fallback) {
  const data = e?.response?.data;
  const d = data?.detail;
  return (
    e?.userMessage
    || data?.error?.message
    || d?.error?.message || d?.message || (typeof d === 'string' ? d : null)
  ) || fallback;
}

function storeSession(data) {
  if (data?.access_token) localStorage.setItem('access_token', data.access_token);
  if (data?.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
}

// One-time recovery-code display. Codes live only in React state — never in
// localStorage/sessionStorage/URLs — and disappear when acknowledged.
function RecoveryCodesPanel({ codes, onDone }) {
  const copyAll = () => {
    navigator.clipboard?.writeText(codes.join('\n')).then(() => toast.success('Recovery codes copied'));
  };
  return (
    <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-4" data-testid="2fa-recovery-codes">
      <div className="flex items-start gap-2 text-amber-800">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
        <p className="text-sm font-medium">Save these recovery codes now. They are shown only once and cannot be retrieved again. Each code works exactly once.</p>
      </div>
      <div className="grid grid-cols-2 gap-1.5 font-mono text-sm" role="list" aria-label="Recovery codes">
        {codes.map((c) => <div key={c} role="listitem" className="rounded bg-white border px-2 py-1">{c}</div>)}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={copyAll} data-testid="2fa-copy-codes"><Copy className="h-3.5 w-3.5 mr-1" aria-hidden />Copy</Button>
        <Button size="sm" onClick={onDone} data-testid="2fa-codes-saved">I have saved my recovery codes</Button>
      </div>
    </div>
  );
}

function EnrolmentPanel({ enrolment, onConfirmed, onCancelled }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post('/auth/2fa/enrol/confirm', { code });
      storeSession(r.data.data);
      onConfirmed(r.data.data.recovery_codes || []);
    } catch (e) { toast.error(errMsg(e, 'Unable to confirm enrolment')); } finally { setBusy(false); }
  };
  const cancel = async () => {
    try { await api.post('/auth/2fa/enrol/cancel'); } catch { /* already gone */ }
    onCancelled();
  };
  return (
    <div className="space-y-3" data-testid="2fa-enrolment-panel">
      <p className="text-sm text-slate-600">Scan this QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, Authy, 1Password…), or enter the setup key manually. Then enter the 6-digit code from the app to finish.</p>
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <img src={enrolment.qr_data_uri} alt="Authenticator QR code" className="h-44 w-44 border rounded-md bg-white" data-testid="2fa-qr" />
        <div className="space-y-2 text-sm">
          <div className="text-xs text-slate-500">Manual setup key</div>
          <code className="block rounded bg-slate-100 px-2 py-1.5 font-mono text-xs break-all" data-testid="2fa-manual-key">{enrolment.manual_key}</code>
          <div className="text-xs text-slate-500">Issuer: {enrolment.issuer} · SHA-1 · 6 digits · 30 s</div>
          <div className="text-xs text-amber-700" data-testid="2fa-enrol-expiry">This setup expires at {new Date(enrolment.expires_at).toLocaleTimeString()}. Do not share the key or QR code with anyone.</div>
        </div>
      </div>
      <label className="text-xs text-slate-600 space-y-1 block max-w-xs">Code from your authenticator app
        <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
               onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
               data-testid="2fa-confirm-code" aria-label="Authenticator code" />
      </label>
      <div className="flex gap-2">
        <Button onClick={confirm} disabled={busy || code.length !== 6} data-testid="2fa-confirm-btn">Verify and enable</Button>
        <Button variant="outline" onClick={cancel} disabled={busy} data-testid="2fa-cancel-btn">Cancel</Button>
      </div>
    </div>
  );
}

function PasswordPrompt({ label, onSubmit, busy, testId }) {
  const [password, setPassword] = useState('');
  return (
    <div className="space-y-2 max-w-xs">
      <label className="text-xs text-slate-600 space-y-1 block">Confirm your current password to continue
        <Input type="password" autoComplete="current-password" value={password}
               onChange={(e) => setPassword(e.target.value)} data-testid={`${testId}-password`} aria-label="Current password" />
      </label>
      <Button onClick={() => onSubmit(password)} disabled={busy || !password} data-testid={`${testId}-submit`}>{label}</Button>
    </div>
  );
}

function EnabledPanel({ status, reload, onCodes }) {
  const [mode, setMode] = useState(null); // 'regenerate' | 'disable'
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [method, setMethod] = useState('totp');
  const [busy, setBusy] = useState(false);

  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post('/auth/2fa/recovery-codes/regenerate', { password, code });
      storeSession(r.data.data);
      onCodes(r.data.data.recovery_codes || []);
      setMode(null); setPassword(''); setCode('');
    } catch (e) { toast.error(errMsg(e, 'Unable to regenerate recovery codes')); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      const r = await api.post('/auth/2fa/disable', { password, method, code, confirm: true });
      storeSession(r.data.data);
      toast.success('Two-factor authentication disabled');
      setMode(null); setPassword(''); setCode('');
      reload();
    } catch (e) { toast.error(errMsg(e, 'Unable to disable two-factor authentication')); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="2fa-enabled-panel">
      <div className="flex items-center gap-2 text-emerald-700 text-sm">
        <ShieldCheck className="h-4 w-4" aria-hidden />
        Two-factor authentication is <span className="font-semibold">enabled</span>
        {status.enabled_at && <span className="text-xs text-slate-500">since {new Date(status.enabled_at).toLocaleDateString()}</span>}
      </div>
      <div className="text-xs text-slate-600" data-testid="2fa-recovery-remaining">
        Recovery codes remaining: <span className="font-semibold">{status.recovery_codes_remaining}</span>
        {status.recovery_codes_remaining <= 3 && <span className="text-amber-700 ml-1">— consider regenerating soon.</span>}
      </div>
      {status.reset_pending && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="2fa-reset-pending">
          An administrator reset of your two-factor authentication is awaiting approval.
        </div>
      )}
      {!mode && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setMode('regenerate')} data-testid="2fa-regenerate-btn">
            <RefreshCcw className="h-3.5 w-3.5 mr-1" aria-hidden />Regenerate recovery codes
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setMode('disable')} data-testid="2fa-disable-btn">Disable two-factor</Button>
        </div>
      )}
      {mode && (
        <div className="space-y-2 max-w-sm border rounded-md p-3">
          <div className="text-sm font-medium">{mode === 'regenerate' ? 'Regenerate recovery codes' : 'Disable two-factor authentication'}</div>
          {mode === 'disable' && <p className="text-xs text-slate-500">Your account will no longer require an authenticator code at sign-in. All recovery codes will be invalidated.</p>}
          <label className="text-xs text-slate-600 space-y-1 block">Current password
            <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="2fa-action-password" aria-label="Current password" />
          </label>
          {mode === 'disable' && (
            <div className="flex gap-3 text-xs" role="radiogroup" aria-label="Verification method">
              <label className="flex items-center gap-1"><input type="radio" checked={method === 'totp'} onChange={() => setMethod('totp')} data-testid="2fa-method-totp" />Authenticator code</label>
              <label className="flex items-center gap-1"><input type="radio" checked={method === 'recovery_code'} onChange={() => setMethod('recovery_code')} data-testid="2fa-method-recovery" />Recovery code</label>
            </div>
          )}
          <label className="text-xs text-slate-600 space-y-1 block">{mode === 'disable' && method === 'recovery_code' ? 'Recovery code' : 'Fresh authenticator code'}
            <Input inputMode={method === 'totp' || mode === 'regenerate' ? 'numeric' : 'text'} autoComplete="one-time-code" maxLength={mode === 'disable' && method === 'recovery_code' ? 12 : 6}
                   value={code} onChange={(e) => setCode(mode === 'disable' && method === 'recovery_code' ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ''))}
                   data-testid="2fa-action-code" aria-label="Verification code" />
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={mode === 'regenerate' ? regenerate : disable} disabled={busy || !password || !code}
                    variant={mode === 'disable' ? 'destructive' : 'default'} data-testid="2fa-action-submit">
              {mode === 'regenerate' ? 'Regenerate' : 'Disable two-factor'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setMode(null); setPassword(''); setCode(''); }} data-testid="2fa-action-cancel">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TwoFactorSettings({ settings }) {
  const [status, setStatus] = useState(null);
  const [available, setAvailable] = useState(settings?.two_factor?.available === true);
  const [enrolment, setEnrolment] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showBegin, setShowBegin] = useState(false);

  const reload = useCallback(() => {
    api.get('/auth/2fa/status')
      .then((r) => { setAvailable(true); setStatus(r.data.data); })
      .catch((e) => { if (e?.response?.status === 404) setAvailable(false); else toast.error(errMsg(e, 'Unable to load 2FA status')); });
  }, []);
  useEffect(() => { if (settings?.two_factor?.available) reload(); }, [reload, settings]);

  const begin = async (password) => {
    setBusy(true);
    try {
      const r = await api.post('/auth/2fa/enrol/begin', { password });
      setEnrolment(r.data.data);
      setShowBegin(false);
    } catch (e) { toast.error(errMsg(e, 'Unable to start enrolment')); } finally { setBusy(false); }
  };

  return (
    <Card data-testid="settings-2fa">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Lock className="h-4 w-4" aria-hidden />Two-factor authentication
          {!available && <Badge variant="outline" className="text-[10px]">Not available</Badge>}
          {available && status?.enabled && <Badge className="text-[10px] bg-emerald-600">Enabled</Badge>}
          {available && status && !status.enabled && <Badge variant="outline" className="text-[10px]">Optional</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!available && (
          <div className="text-sm text-slate-500" data-testid="2fa-unavailable">
            {settings?.two_factor?.message || 'Two-factor authentication is not available in this release.'}
          </div>
        )}
        {available && !status && <div className="text-sm text-slate-400">Loading…</div>}
        {available && recoveryCodes && (
          <RecoveryCodesPanel codes={recoveryCodes} onDone={() => { setRecoveryCodes(null); setEnrolment(null); reload(); }} />
        )}
        {available && status && !recoveryCodes && (
          <>
            {enrolment && !status.enabled && (
              <EnrolmentPanel enrolment={enrolment}
                              onConfirmed={(codes) => setRecoveryCodes(codes)}
                              onCancelled={() => { setEnrolment(null); reload(); }} />
            )}
            {!enrolment && !status.enabled && (
              <div className="space-y-3" data-testid="2fa-not-enrolled">
                <p className="text-sm text-slate-600">Add an extra layer of security to your account. When enabled, signing in requires a 6-digit code from an authenticator app in addition to your password. Enrolment is optional.</p>
                {!showBegin && <Button size="sm" onClick={() => setShowBegin(true)} data-testid="2fa-begin-btn">Set up two-factor authentication</Button>}
                {showBegin && <PasswordPrompt label="Continue" onSubmit={begin} busy={busy} testId="2fa-begin" />}
              </div>
            )}
            {status.enabled && <EnabledPanel status={status} reload={reload} onCodes={(codes) => setRecoveryCodes(codes)} />}
          </>
        )}
      </CardContent>
    </Card>
  );
}
